import { useCreatorSearch } from '@round/creatorbase-helpers/api-hooks';
import { useInstagramUserImages } from '@round/platforms/instagram';
import { useTikTokUserImages } from '@round/platforms/tiktok';
import { ClientInfluencerAccount, ClientInfluencerAccountStats } from './helpers';

type Config = { enabled?: boolean };

/**
 * @param clientInfluencerId - ClientInfluencerAccount.client_influencer_id. An id that groups ClientInfluencerAccounts.
 */
export default function useCreatorData(clientInfluencerId: string | undefined, config?: Config) {
    const {
        data: creatorSearchData,
        isLoading: isCreatorSearchLoading,
        isPending: isCreatorSearchPending,
    } = useCreatorSearch(
        {
            client_influencer_id: clientInfluencerId,
        },
        config
    );

    const creatorSearchResult = creatorSearchData?.results[0] ?? null;
    const creatorInfluencerAccounts: ClientInfluencerAccount[] = [
        ...(creatorSearchResult?.platforms.instagram ?? []).map((account) => ({
            ...account,
            platform: 'instagram' as const,
        })),
        ...(creatorSearchResult?.platforms.tiktok ?? []).map((account) => ({
            ...account,
            platform: 'tiktok' as const,
        })),
    ];

    const tiktokUserIds = creatorInfluencerAccounts
        .filter((account) => account.platform === 'tiktok')
        .map((account) => account.creatorbase_user_id);
    const { data: tiktokUserImages, isLoading: tiktokUserImagesLoading } = useTikTokUserImages(tiktokUserIds, {
        enabled: !!tiktokUserIds.length,
    });

    const instagramUserIds = creatorInfluencerAccounts
        .filter((account) => account.platform === 'instagram')
        .map((account) => account.creatorbase_user_id);
    const { data: instagramUserImages, isLoading: instagramUserImagesLoading } = useInstagramUserImages(
        instagramUserIds,
        {
            enabled: !!instagramUserIds.length,
        }
    );

    const getAccountStats = (account: ClientInfluencerAccount | undefined): ClientInfluencerAccountStats | null => {
        if (!account) {
            return null;
        }

        return {
            followers: account.metrics.total_follower_count,
            engagementRate: account.metrics.avg_engagement_rate,
            averageViews: account.metrics.avg_video_views_per_post,
            cpm: account.estimated_cpm,
            currencyId: account.currency_id,
            avgDraftTimeInHours: creatorSearchResult?.metrics.avg_draft_time_in_hours ?? null,
        };
    };

    return {
        accounts: creatorInfluencerAccounts,
        isAccountsLoading: isCreatorSearchLoading,
        isAccountsPending: isCreatorSearchPending,
        getAccount: (accountId: number | undefined) => {
            const account = creatorInfluencerAccounts.find(
                (account) => account.creatorbase_client_influencer_account_id === accountId
            );

            return { account, stats: getAccountStats(account), isLoading: isCreatorSearchLoading };
        },
        getProfileImage: (account: ClientInfluencerAccount | undefined) => {
            if (!account) {
                return null;
            }

            const image =
                account.platform === 'tiktok'
                    ? tiktokUserImages?.find((image) => image.user_id === account.creatorbase_user_id)
                    : instagramUserImages?.find((image) => image.user_id === account.creatorbase_user_id);
            const imageUrl = image?.avatar_thumb.cached_url ?? image?.avatar_thumb.original_url ?? null;
            const isLoading = account.platform === 'tiktok' ? tiktokUserImagesLoading : instagramUserImagesLoading;
            return { imageUrl, isLoading };
        },
    };
}
